import { ComponentSwitcherDirective } from '@anecdotes/design-system/component-switcher';
import { RichTextareaControl, TextFieldControl } from '@anecdotes/design-system/form-controls';
import { ModalWindowService } from '@anecdotes/design-system/modals';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { DynamicFormGroup } from '@anecdotes/dynamic-form';
import { Subject } from 'rxjs';
import { TrustCenterModalEnum } from '../../../../constants';
import { TrustCenterInfoResponse, TrustCenterFacadeService } from '@anecdotes/shared-trust-center';
import { CustomValidators } from '@anecdotes/validators';

enum FormControls {
  Title = 'title',
  Overview = 'overview',
}

interface InitialValues {
  [FormControls.Title]: string;
  [FormControls.Overview]: string;
}

@Component({
  selector: 'app-overview-edit-side-panel',
  templateUrl: './overview-edit-side-panel.component.html',
  styleUrls: ['./overview-edit-side-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OverviewEditSidePanelComponent implements OnInit {
  readonly sendingRequestBtnLoader$ = new Subject<boolean>();

  @HostBinding('class')
  private readonly classes = 'h-full';

  private initialFormValues: InitialValues;

  form: DynamicFormGroup<any>;

  @Input()
  trustCenterInfo: TrustCenterInfoResponse;

  get wasFormValueChanged(): boolean {
    if (!this.form) {
      return false;
    }

    return Object.values(FormControls).some((name) => this.form.items[name].value !== this.initialFormValues[name]);
  }

  constructor(
    private trustCenterFacadeService: TrustCenterFacadeService,
    private modalWindowService: ModalWindowService,
    private switcher: ComponentSwitcherDirective,
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  async save(): Promise<void> {
    this.sendingRequestBtnLoader$.next(true);

    try {
      await this.trustCenterFacadeService.updateTrustCenterInfo({
        title: this.form.controls[FormControls.Title].value,
        overview: this.form.controls[FormControls.Overview].value,
      });
      this.modalWindowService.close();
    } catch {
      this.switcher.goById(TrustCenterModalEnum.ErrorModal);
    } finally {
      this.sendingRequestBtnLoader$.next(false);
    }
  }

  private initForm(): void {
    this.form = new DynamicFormGroup({
      [FormControls.Title]: new TextFieldControl({
        initialInputs: {
          validateOnDirty: true,
          required: true,
          label: this.buildTranslationKey('overview.titleFieldName'),
          displayCharactersCounter: true,
          maxLength: 70,
        },
        validators: [Validators.required, Validators.maxLength(70)],
        initialValue: this.trustCenterInfo.title,
      }),
      [FormControls.Overview]: new RichTextareaControl({
        initialInputs: {
          validateOnDirty: true,
          required: true,
          label: this.buildTranslationKey('overview.textFieldName'),
        },
        initialValue: this.trustCenterInfo.overview,
        validators: [CustomValidators.requiredRichTextarea],
      }),
    });

    this.initialFormValues = this.form.value;
  }
}
